import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';

/**
 * Result of a company-register lookup, proxied through the backend
 * `/api/company-register/lookup` route (AG-23a) — see
 * `webyto_api-express4-TS-20250331/docs/PROMPT_company-register-lookup.md`
 * for the authoritative shape. Mirror that shape exactly; do not add
 * fields the backend doesn't return.
 */
export interface CompanyRegisterResult {
  found: boolean;
  registrationCode: string;
  officialName: string | null;
  shortName: string | null;
  status: string | null;
  legalAddress: string | null;
  director: string | null;
  registrationDate: string | null;
  provider: string;
  /** Only meaningful when `found` is `false`. `'access_restricted'` means
   *  the company is real and registered but the vendor has hidden its data
   *  at the subject's own request (confirmed live against opendatabot,
   *  Постанова КМУ № 1257) — render a distinct message, never the generic
   *  "not found" one, for this case. `null` means either `found` is
   *  `true`, or the provider couldn't distinguish a reason. */
  notFoundReason: 'not_registered' | 'access_restricted' | null;
  raw: unknown;   // never rendered directly in the template — debugging aid only
  /**
   * VAT/taxation data embedded directly on the lookup result — added
   * 2026-09-01, opendatabot only so far. `undefined` (field absent) means
   * this provider doesn't embed it (youcontrol: VAT is a separate paid call,
   * see `getVatInfo()` below); `null` means the provider embeds it but found
   * nothing for this code. Check `result.vatInfo !== undefined` before
   * falling back to `getVatInfo()` — see `onVerify()` in
   * `company-register-verify.component.ts` for the actual precedence logic.
   */
  vatInfo?: VatPayerInfo | null;
  /** Same embedded/`undefined`-means-not-supported convention as `vatInfo`
   *  above. No separate-call equivalent exists for this — youcontrol has no
   *  known taxation-mode field at all yet. */
  taxationMode?: TaxationModeInfo | null;
}

/**
 * Result of a VAT/tax-payer lookup. Two provenances share this shape:
 * - youcontrol: `POST /api/company-register/vat` — a separate paid vendor
 *   call from `lookup()`, only meaningful when the configured backend
 *   provider implements it.
 * - opendatabot: embedded directly on `CompanyRegisterResult.vatInfo` from
 *   `lookup()` itself — see that field's doc comment. No separate call.
 * See `webyto_api-express4-TS-20250331`'s `services/company-register/types.ts`
 * for the authoritative shape and the per-provider field provenance.
 */
export interface VatPayerInfo {
  found: boolean;
  registrationCode: string;
  /** Individual tax number of the VAT payer (ІПН платника ПДВ). */
  vatNumber: string | null;
  registrationDate: string | null;
  /** Vendor's own status/note text, passed through as-is — not normalized
   *  to an enum, same convention as `CompanyRegisterResult.status`.
   *  youcontrol only — always `null` for opendatabot. */
  note: string | null;
  /** Vendor's own "as of" field. youcontrol only — always `null` for
   *  opendatabot. **Do not use this for display or composition** — caught
   *  live twice (2026-08-31, 2026-09-01, two different real companies)
   *  always returning today's/near-today's date instead of a stable
   *  freshness marker, even with `showCurrentData` forced on. Kept here
   *  only as a pass-through of the vendor's raw field; `registrationDate`
   *  is the value actually used everywhere (see `onRegisterVerifyApply()`
   *  in `edit-client.component.ts`). */
  actualDate: string | null;
  /** Date the VAT registration was cancelled, if it was. opendatabot only —
   *  always `null` for youcontrol. */
  cancellationDate: string | null;
  provider: string;
  raw: unknown;
}

/**
 * Taxation-system snapshot — added 2026-09-01, opendatabot only so far,
 * embedded on `CompanyRegisterResult.taxationMode` from `lookup()` (no
 * separate call). youcontrol has no known equivalent field yet.
 */
export interface TaxationModeInfo {
  found: boolean;
  registrationCode: string;
  generalSystem: boolean | null;
  singleTaxGroup: number | null;
  singleTaxRate: number | null;
  singleTaxActive: boolean | null;
  provider: string;
  raw: unknown;
}

/** One entry from `GET /api/company-register/providers` — a registry
 *  service that actually has credentials configured server-side. `stub`
 *  never appears here even if it's the deployment's silent default. */
export interface CompanyRegisterProviderOption {
  name: string;
}

/**
 * REST client for AG-23a's company-register lookup route
 * (`/api/company-register/lookup`, webyto_api-express4-TS-20250331).
 *
 * Modeled on `external-api.service.ts`, not `agent-data-api.service.ts` —
 * the backend route is un-DB-backed (no `extractDatabaseContext`), so this
 * deliberately sends only `Content-Type`, not the full
 * `getStandardApiHeaders()`/`Webyto-Database-*` set every DB-backed service
 * needs. See `docs/PROMPT_company-register-verify.md`'s "What already
 * exists" section for the full reasoning.
 */
@Injectable({
  providedIn: 'root'
})
export class CompanyRegisterApiService {
  apiPath: string = '/api/company-register';

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    })
  };

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) { }

  private _getApiUrl(url: string = ''): string {
    return this.globalsettingsservice.getApiServerUrl() + this.apiPath + url;
  }

  /**
   * `GET /api/company-register/providers` — added 2026-09-01. Lists the
   * registry services actually available to pick from (credentialed
   * server-side), for the verify button's provider-picker menu.
   */
  getAvailableProviders(): Observable<{ providers: CompanyRegisterProviderOption[] }> {
    return this.http.get<{ providers: CompanyRegisterProviderOption[] }>(
      this._getApiUrl('/providers'),
      this.httpOptions
    ).pipe(retry(1));
  }

  /**
   * `POST /api/company-register/lookup` — idempotent read despite the
   * verb (no server-side write happens), so retried once like the other
   * read call sites in `agent-data-api.service.ts`.
   *
   * `provider` (added 2026-09-01) targets one specific registry service
   * for this call — one of `getAvailableProviders()`'s names. Omitted
   * falls back to the backend's deployment-wide default
   * (`COMPANY_REGISTER_PROVIDER`), exactly as before this parameter
   * existed.
   */
  lookup(registrationCode: string, provider?: string): Observable<CompanyRegisterResult> {
    return this.http.post<CompanyRegisterResult>(
      this._getApiUrl('/lookup'),
      provider ? { registrationCode, provider } : { registrationCode },
      this.httpOptions
    ).pipe(retry(1));
  }

  /**
   * `POST /api/company-register/vat` — same idempotent-read/retry
   * treatment as `lookup()`. Returns `501` if the resolved provider
   * doesn't implement VAT lookup (not every provider does) — callers must
   * handle that as a normal HTTP error, same fail-open principle as
   * `lookup()`'s own failures. Same `provider` param as `lookup()` above —
   * always pass the SAME provider used for the paired `lookup()` call, see
   * `onVerify()` in `company-register-verify.component.ts`.
   */
  getVatInfo(registrationCode: string, provider?: string): Observable<VatPayerInfo> {
    return this.http.post<VatPayerInfo>(
      this._getApiUrl('/vat'),
      provider ? { registrationCode, provider } : { registrationCode },
      this.httpOptions
    ).pipe(retry(1));
  }
}
